import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TenantProfileApi, type TenantProfile, type TenantProfilePatch } from '@/lib/api/v1';
import {
  Badge, Button, Icon, Input, SectionCard, Skeleton, Switch, Textarea,
} from '@/design-system';
import { useToasts } from '@/lib/store';
import { UpgradeGate, isEntitlementError } from '@/components/UpgradeGate';

/**
 * "/profil" — the authed editor for the tenant's public restaurant page,
 * cloned from Vinify's public restaurant profile (hero photo, name +
 * address + directions, booking/website/menu/Instagram buttons,
 * description, and a Vinlister section). The public page itself is
 * server-rendered at /p/:profileSlug (see backend routes/tenant-profile-public.ts)
 * and needs no auth — this page is only the editor.
 */
export function ProfilPage() {
  const qc = useQueryClient();
  const { push } = useToasts();
  const profileQ = useQuery({ queryKey: ['tenant-profile'], queryFn: () => TenantProfileApi.get() });

  const patch = useMutation({
    mutationFn: (payload: TenantProfilePatch) => TenantProfileApi.update(payload),
    onSuccess: (updated) => qc.setQueryData(['tenant-profile'], updated),
    onError: (err: Error) => push({ title: 'Kunne ikke lagre', description: err.message, tone: 'danger' }),
  });

  const uploadHero = useMutation({
    mutationFn: (file: File) => TenantProfileApi.uploadHeroImage(file),
    onSuccess: ({ profile }) => {
      qc.setQueryData(['tenant-profile'], profile);
      push({ title: 'Bilde lastet opp', tone: 'success' });
    },
    onError: (err: Error) => push({ title: 'Kunne ikke laste opp bilde', description: err.message, tone: 'danger' }),
  });

  if (isEntitlementError(profileQ.error)) {
    return <UpgradeGate error={profileQ.error} featureLabel="Offentlig side" />;
  }

  if (profileQ.isLoading || !profileQ.data) {
    return (
      <div className="grid gap-4 max-w-3xl">
        <Skeleton height={40} rounded="md" />
        <Skeleton height={220} rounded="lg" />
        <Skeleton height={320} rounded="lg" />
      </div>
    );
  }

  const profile = profileQ.data;

  return (
    <div className="grid gap-6 max-w-3xl pb-16">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="ui-section-title text-3xl">Offentlig side</h1>
          <p className="text-sm text-mist">
            En enkel offentlig side for stedet ditt — bilde, adresse, lenker og vinlistene dine. Klon av Vinifys
            restaurantside.
          </p>
        </div>
        <Badge tone="gold">Pro</Badge>
      </header>

      <PublishCard profile={profile} onTogglePublish={(isPublished) => patch.mutate({ isPublished })} saving={patch.isPending} />

      <HeroImageCard profile={profile} onUpload={(file) => uploadHero.mutate(file)} uploading={uploadHero.isPending} />

      <DetailsCard profile={profile} onSave={(payload) => patch.mutate(payload)} saving={patch.isPending} />

      <LinksCard profile={profile} onSave={(payload) => patch.mutate(payload)} saving={patch.isPending} />
    </div>
  );
}

// ─── Publish + slug ─────────────────────────────────────────────────────
function PublishCard({ profile, onTogglePublish, saving }: {
  profile: TenantProfile;
  onTogglePublish: (isPublished: boolean) => void;
  saving: boolean;
}) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [slug, setSlug] = useState(profile.profileSlug ?? '');
  const [availability, setAvailability] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => setSlug(profile.profileSlug ?? ''), [profile.profileSlug]);

  const patch = useMutation({
    mutationFn: (payload: TenantProfilePatch) => TenantProfileApi.update(payload),
    onSuccess: (updated) => {
      qc.setQueryData(['tenant-profile'], updated);
      push({ title: 'Adresse lagret', tone: 'success' });
    },
    onError: (err: Error) => push({ title: 'Kunne ikke lagre adresse', description: err.message, tone: 'danger' }),
  });

  function checkAvailability(value: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const normalized = value.trim().toLowerCase();
    if (!normalized || normalized === profile.profileSlug) {
      setAvailability('idle');
      return;
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized) || normalized.length < 3) {
      setAvailability('invalid');
      return;
    }
    setAvailability('checking');
    debounceRef.current = setTimeout(() => {
      TenantProfileApi.slugAvailability(normalized)
        .then((res) => setAvailability(res.available ? 'available' : 'taken'))
        .catch(() => setAvailability('idle'));
    }, 400);
  }

  const canPublish = Boolean(profile.profileSlug);

  return (
    <SectionCard
      title="Publisering"
      description="Adressen bestemmer den offentlige lenken din. Siden må ha en adresse før den kan publiseres."
    >
      <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <Input
          label="Adresse (slug)"
          value={slug}
          onChange={(e) => {
            const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
            setSlug(value);
            checkAvailability(value);
          }}
          onBlur={() => {
            const normalized = slug.trim();
            if (normalized && normalized !== profile.profileSlug && (availability === 'available' || availability === 'idle')) {
              patch.mutate({ profileSlug: normalized });
            }
          }}
          placeholder="f.eks. sinck-amsterdam"
          hint={
            availability === 'checking'
              ? 'Sjekker tilgjengelighet …'
              : availability === 'taken'
                ? 'Denne adressen er allerede i bruk.'
                : availability === 'invalid'
                  ? 'Bruk små bokstaver, tall og bindestrek (minst 3 tegn).'
                  : availability === 'available'
                    ? 'Ledig!'
                    : undefined
          }
        />
        <Switch
          checked={profile.isPublished}
          onChange={(next) => {
            if (next && !canPublish) {
              push({ title: 'Sett en adresse først', description: 'Siden trenger en adresse (slug) før den kan publiseres.', tone: 'warning' });
              return;
            }
            onTogglePublish(next);
          }}
          label={profile.isPublished ? 'Publisert' : 'Ikke publisert'}
        />
      </div>

      {profile.isPublished && profile.publicUrl && (
        <div className="flex items-center gap-2 rounded-md border border-line-soft bg-overlay-soft px-3 py-2">
          <Icon name="globe" size={14} className="text-candle shrink-0" />
          <a href={profile.publicUrl} target="_blank" rel="noreferrer" className="truncate text-sm text-candle hover:underline">
            {profile.publicUrl}
          </a>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto shrink-0"
            iconLeft="copy"
            onClick={() => {
              void navigator.clipboard.writeText(profile.publicUrl!);
              push({ title: 'Lenke kopiert', tone: 'success' });
            }}
          >
            Kopier
          </Button>
          <a href={profile.publicUrl} target="_blank" rel="noreferrer" className="ui-btn ui-btn--primary ui-btn--sm shrink-0">
            Åpne offentlig side
          </a>
        </div>
      )}
      {saving && <p className="text-xs text-mist">Lagrer …</p>}
    </SectionCard>
  );
}

// ─── Hero image ─────────────────────────────────────────────────────────
function HeroImageCard({ profile, onUpload, uploading }: {
  profile: TenantProfile;
  onUpload: (file: File) => void;
  uploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <SectionCard title="Bilde" description="Vises øverst på den offentlige siden.">
      <div className="grid gap-3">
        <div
          className="relative flex aspect-[16/7] w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-line-soft bg-overlay-soft"
          style={
            profile.heroImageUrl
              ? { backgroundImage: `url(${profile.heroImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : undefined
          }
        >
          {!profile.heroImageUrl && (
            <div className="grid place-items-center gap-1 text-center text-mist">
              <Icon name="image" size={28} />
              <span className="text-xs">Ingen bilde lastet opp</span>
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 grid place-items-center bg-ink/60">
              <Icon name="spinner" size={22} className="animate-spin text-candle" />
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = '';
          }}
        />
        <Button variant="ghost" iconLeft="upload" onClick={() => inputRef.current?.click()} loading={uploading}>
          {profile.heroImageUrl ? 'Bytt bilde' : 'Last opp bilde'}
        </Button>
        <p className="text-xs text-mist">JPG, PNG, WEBP eller GIF — opp til 8 MB.</p>
      </div>
    </SectionCard>
  );
}

// ─── Name / description / address ──────────────────────────────────────
function DetailsCard({ profile, onSave, saving }: {
  profile: TenantProfile;
  onSave: (payload: TenantProfilePatch) => void;
  saving: boolean;
}) {
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [description, setDescription] = useState(profile.description);
  const [address, setAddress] = useState(profile.address);
  useEffect(() => setDisplayName(profile.displayName), [profile.displayName]);
  useEffect(() => setDescription(profile.description), [profile.description]);
  useEffect(() => setAddress(profile.address), [profile.address]);

  return (
    <SectionCard title="Om stedet">
      <Input
        label="Navn"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        onBlur={() => { if (displayName !== profile.displayName) onSave({ displayName }); }}
        placeholder="F.eks. Sinck Amsterdam"
      />
      <Input
        label="Adresse (vises på siden)"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        onBlur={() => { if (address !== profile.address) onSave({ address }); }}
        placeholder="Gate, postnummer, sted"
        hint="Brukes også til «Veibeskrivelse»-lenken (Google Maps)."
      />
      <Textarea
        label="Beskrivelse"
        rows={4}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={() => { if (description !== profile.description) onSave({ description }); }}
        placeholder="Kort tekst om stedet …"
      />
      {saving && <p className="text-xs text-mist">Lagrer …</p>}
    </SectionCard>
  );
}

// ─── Outbound links ──────────────────────────────────────────────────────
function LinksCard({ profile, onSave, saving }: {
  profile: TenantProfile;
  onSave: (payload: TenantProfilePatch) => void;
  saving: boolean;
}) {
  const [bookingUrl, setBookingUrl] = useState(profile.bookingUrl ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(profile.websiteUrl ?? '');
  const [menuUrl, setMenuUrl] = useState(profile.menuUrl ?? '');
  const [instagramUrl, setInstagramUrl] = useState(profile.instagramUrl ?? '');
  useEffect(() => setBookingUrl(profile.bookingUrl ?? ''), [profile.bookingUrl]);
  useEffect(() => setWebsiteUrl(profile.websiteUrl ?? ''), [profile.websiteUrl]);
  useEffect(() => setMenuUrl(profile.menuUrl ?? ''), [profile.menuUrl]);
  useEffect(() => setInstagramUrl(profile.instagramUrl ?? ''), [profile.instagramUrl]);

  return (
    <SectionCard title="Lenker" description="Kun knappene med utfylt lenke vises på den offentlige siden.">
      <Input
        label="Bestill bord"
        iconLeft="calendar"
        value={bookingUrl}
        onChange={(e) => setBookingUrl(e.target.value)}
        onBlur={() => { if (bookingUrl !== (profile.bookingUrl ?? '')) onSave({ bookingUrl: bookingUrl || null }); }}
        placeholder="https://…"
      />
      <Input
        label="Nettside"
        iconLeft="globe"
        value={websiteUrl}
        onChange={(e) => setWebsiteUrl(e.target.value)}
        onBlur={() => { if (websiteUrl !== (profile.websiteUrl ?? '')) onSave({ websiteUrl: websiteUrl || null }); }}
        placeholder="https://…"
      />
      <Input
        label="Meny"
        iconLeft="list"
        value={menuUrl}
        onChange={(e) => setMenuUrl(e.target.value)}
        onBlur={() => { if (menuUrl !== (profile.menuUrl ?? '')) onSave({ menuUrl: menuUrl || null }); }}
        placeholder="https://…"
      />
      <Input
        label="Instagram"
        iconLeft="external"
        value={instagramUrl}
        onChange={(e) => setInstagramUrl(e.target.value)}
        onBlur={() => { if (instagramUrl !== (profile.instagramUrl ?? '')) onSave({ instagramUrl: instagramUrl || null }); }}
        placeholder="https://instagram.com/…"
      />
      {saving && <p className="text-xs text-mist">Lagrer …</p>}
    </SectionCard>
  );
}
